import React, { Fragment, useState, useEffect, useContext } from "react";
import "./TeamsRecordingSearchResult.scss";
import { urls } from "../../logic/urls";
import { thumbnailProvider } from "../../logic/thumbnailProvider";
import { toDisplayString } from "../../logic/displayString";
import {
  ArrowLeft32Filled as ArrowLeftIcon,
  ArrowRight32Filled as ArrowRightIcon,
} from "@fluentui/react-icons";
import { Button } from "@fluentui/react-components";
import MediaActionsMenu from "../common/menu/MediaActions";
import { onDownload } from "../../logic/downloadHandler";
import { useLocation } from "react-router-dom";
import { DataContext } from '../../context/DataProviderContext';

const TOKEN_VALID_TIME_SECONDS = 3600;

interface ContentReference {
  type: string;
  reference: string;
}

interface TeamsRecordingSearchResultItem {
  mediaId: string;
  mediaTypes: string[];
  playAllowed: boolean;
  contentReferences: ContentReference[];
  showArrow?: boolean;
  showLeftArrow?: boolean;
  ownerName?: string;
  otherParticipantName?: string;
  topLine?: string;
  subline?: string;
  startTimeText?: string;
  durationText?: string;
  sourceType?: string;
  hasNoAudio?: boolean;
  hasVideo?: boolean;
  hasScreenSharing?: boolean;
}

interface TeamsRecordingSearchResultProps {
  item: TeamsRecordingSearchResultItem;
  onClick: (mediaId: string) => void;
}

export const TeamsRecordingSearchResult: React.FC<TeamsRecordingSearchResultProps> = ({ item, onClick }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const dataContext = useContext(DataContext);
  const location = useLocation();

  useEffect(() => {
    async function getThumbnail() {
      if (!dataContext) return;
      if (!item.mediaTypes.includes("video") && !item.mediaTypes.includes("screen")) {
        setThumbnailUrl(urls.thumbnailAudio);
        return;
      }
      if (item.playAllowed === false) {
        setThumbnailUrl(urls.thumbnailNoContentReference);
        return;
      }
      let thumbnailReference = item.contentReferences.find((item) => item.type === "thumbnail");
      if (!thumbnailReference) {
        setThumbnailUrl(urls.thumbnailNoContentReference);
        return;
      }
      try {
        const objectUrl = await thumbnailProvider.getThumbnail(
          item.mediaId,
          thumbnailReference.reference,
          (mediaId: string, contentReference: string) => dataContext.getMediaContentUrl(mediaId, contentReference, TOKEN_VALID_TIME_SECONDS),
          dataContext.selectMediaContent
        );
        setThumbnailUrl(objectUrl);
        return;
      } catch (error) {
        setThumbnailUrl(urls.thumbnailRetrievalError);
        return;
      }
    }
    getThumbnail();
  }, [
    item.contentReferences,
    item.mediaId,
    item.mediaTypes,
    item.playAllowed,
    dataContext
  ]);

  const handleOnClick = () => {
    if (item.playAllowed) {
      onClick(item.mediaId);
    }
  };
  const handleOnMouseEnter = () => setIsHovered(true);
  const handleOnMouseLeave = () => setIsHovered(false);

  const handleOnDownload = async () => {
    if (!dataContext) return;
    let audioAndVideoReferences = item.contentReferences.filter((reference) => reference.type === 'audio' || reference.type === 'video');
    const contentReference = audioAndVideoReferences[0]?.reference;
    if (!contentReference) return;
    const contentUrl = await dataContext.getMediaContentUrl(item.mediaId, contentReference, TOKEN_VALID_TIME_SECONDS);
    onDownload(contentUrl.url);
  };

  const thumbnailUrlToDisplay = thumbnailUrl !== "" ? thumbnailUrl : urls.thumbnailPlaceholder;
  const showThumbnail = thumbnailUrl !== "";

  let topLineRendered;
  if (item.showArrow) {
    const iconType = item.showLeftArrow ? <ArrowLeftIcon /> : <ArrowRightIcon />;
    topLineRendered = (
      <>
        {item.ownerName}
        <Button appearance="transparent" icon={iconType} style={{ height: "11px", padding: "0" }} />
        {item.otherParticipantName}
      </>
    );
  } else {
    topLineRendered = <>{item?.topLine}</>;
  }

  let sourceTypeIcon: React.ReactNode = null;
  switch (item.sourceType) {
    case "teams":
    case "bodyCam":
    case "cybergate":
      sourceTypeIcon = (
        <div className={`option-icon ${item.sourceType}`} title={toDisplayString(item.sourceType!)} />
      );
      break;
    case "fixedLine":
      sourceTypeIcon = (
        <div className="option-icon material-icons" title={toDisplayString(item.sourceType!)}>
          phone
        </div>
      );
      break;
    case "mobile":
      sourceTypeIcon = (
        <div className="option-icon material-icons" title={toDisplayString(item.sourceType!)}>
          phone_iphone
        </div>
      );
      break;
    default:
      sourceTypeIcon = null;
      break;
  }

  return (
    <Fragment>
      <section
        className="result-item"
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      >
        <div onClick={handleOnClick} className="thumbnail-support">
          <img
            className={`${showThumbnail ? "displayThumbnail" : ""}`}
            src={thumbnailUrlToDisplay}
            alt="Thumbnail"
          />
          {item.playAllowed ? (
            <img
              className={`overlay shadowed`}
              src={urls.thumbnailOverlay}
              alt="Overlay"
            />
          ) : null}
        </div>
        <div className="text-result">
          <h3>{topLineRendered}</h3>
          <div className="row">
            <i>{item.subline}</i>
          </div>
          <div className="row">
            <b>{item.startTimeText}</b>, {item.durationText}
          </div>
          <div className="options row">
            {sourceTypeIcon}
            {item.hasNoAudio ? (
              <div className="option-icon material-icons" title="Audio">
                volume_off
              </div>
            ) : null}
            {item.hasVideo ? (
              <div className="option-icon material-icons" title="Video">
                videocam
              </div>
            ) : null}
            {item.hasScreenSharing ? (
              <div className="option-icon material-icons" title="Screen sharing">
                screen_share
              </div>
            ) : null}
          </div>
        </div>
        <div className="result-item-menu">
          {isHovered && (
            <div>
              <MediaActionsMenu mediaId={item.mediaId} onDownload={handleOnDownload} isHovered={isHovered} location={location.pathname} />
            </div>
          )}
        </div>
      </section>
    </Fragment>
  );
};
